"use client";

import { XIcon } from "lucide-react";

import styles from "./notice-bar.module.css";

/**
 * Dismiss control for the storefront notice bar. The only client JS the bar
 * ships. Persists the dismissal under `storageKey` (a content hash, so a new
 * message re-shows the bar) and hides the bar before the next paint. Storage
 * failures (private mode) are swallowed — the bar just won't stay dismissed.
 */
export function NoticeBarDismiss({
  storageKey,
  label,
  isTop,
}: {
  storageKey: string;
  label: string;
  /** A `top` strip is `position: fixed`, so its reserved space lives on the nav
   *  + main (not the bar). Dismissing it must release that offset — see the
   *  `data-nb-top-dismissed` handling below and in styles/storefront.css. */
  isTop: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => {
        try {
          localStorage.setItem(storageKey, "1");
        } catch {
          /* private mode — no-op */
        }
        document
          .getElementById("tc-notice-bar")
          ?.style.setProperty("display", "none");
        // No visible bottom notice bar anymore → let the transparent header's
        // scrim fade into the hero instead of staying solid (which needs the
        // bar below to absorb its bottom edge). See styles/storefront.css
        // `header[data-notice-below]`.
        document
          .querySelector("header[data-notice-below]")
          ?.removeAttribute("data-notice-below");
        // A `top` strip reserved space on the nav (top-11) + main (pt-[6.75rem]);
        // flag <html> so styles/storefront.css resets both to their no-bar values,
        // reclaiming the gap the hidden fixed strip would otherwise leave.
        if (isTop) {
          document.documentElement.setAttribute("data-nb-top-dismissed", "");
        }
      }}
      className={styles.dismiss}
    >
      <XIcon strokeWidth={1.75} className={styles.dismissIcon} />
    </button>
  );
}
